import React, { useEffect, useState } from 'react';
import portalApi from '../../../../lib/portalApi';
import CreateCalendarItemModal from '../components/CreateCalendarItemModal';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  ExternalLink,
  Clock,
  CheckSquare,
  AlertCircle,
  Trash2,
  X,
} from 'lucide-react';

export default function MentorCalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [items, setItems] = useState([]);
  const [assignedStartups, setAssignedStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [selectedStartupId, setSelectedStartupId] = useState('');
  const [selectedType, setSelectedType] = useState('all'); // 'all' | 'meeting' | 'deadline' | 'task'

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedItemDetail, setSelectedItemDetail] = useState(null);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      setError('');

      // Get month start and end
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const start = new Date(year, month - 1, 1).toISOString();
      const end = new Date(year, month + 2, 0).toISOString();

      const [calRes, startupsRes] = await Promise.all([
        portalApi.getMentorCalendar({
          start,
          end,
          startupId: selectedStartupId || undefined,
          type: selectedType || undefined,
        }),
        portalApi.getMentorStartups(),
      ]);

      setItems(calRes?.items || []);
      setAssignedStartups(startupsRes?.startups || []);
    } catch (err) {
      console.error('Failed to load calendar data:', err);
      setError(err.message || 'Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalendarData();
  }, [currentDate, selectedStartupId, selectedType]);

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Delete / cancel this ${item.type.toLowerCase()}?`)) return;
    try {
      await portalApi.deleteMentorCalendarItem(item.id, item.type);
      setSelectedItemDetail(null);
      loadCalendarData();
    } catch (err) {
      alert(err.message || 'Failed to delete calendar item');
    }
  };

  // Build calendar matrix (Sunday to Saturday or Monday to Sunday)
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  const firstDayIndex = new Date(year, month, 1).getDay(); // 0 is Sunday
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Create date cells
  const daysArray = [];
  for (let i = 0; i < firstDayIndex; i++) {
    daysArray.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    daysArray.push(new Date(year, month, d));
  }

  // Helper: calendar date string YYYY-MM-DD immune to timezone day shift
  const getItemDateStr = (item) => {
    if (!item?.date) return '';
    if (typeof item.date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(item.date)) {
      if (item.type === 'Deadline' || item.type === 'Task') {
        return item.date.slice(0, 10);
      }
    }
    const d = new Date(item.date);
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${da}`;
  };

  const getCellDateStr = (cellDate) => {
    if (!cellDate) return '';
    const yr = cellDate.getFullYear();
    const mo = String(cellDate.getMonth() + 1).padStart(2, '0');
    const da = String(cellDate.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${da}`;
  };

  const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    return getCellDateStr(d1) === getCellDateStr(d2);
  };

  // Group agenda items by calendar date string
  const groupedAgenda = {};
  items.forEach((item) => {
    if (!item.date) return;
    const dateKey = getItemDateStr(item);
    if (!groupedAgenda[dateKey]) groupedAgenda[dateKey] = [];
    groupedAgenda[dateKey].push(item);
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Top Controls Header matching PDF Page 6 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold text-[#F2F2F4] tracking-tight">Calendar</h1>
            <span className="text-sm font-semibold text-[#8E8E99]">{monthName}</span>
          </div>
          <div className="flex items-center gap-1 bg-[#131318] p-1 rounded-xl border border-[#2C2C34]">
            <button
              onClick={prevMonth}
              className="p-1 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4] hover:bg-white/[0.04]"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goToToday}
              className="px-2.5 py-1 text-xs font-medium text-[#8E8E99] hover:text-[#F2F2F4]"
            >
              Today
            </button>
            <button
              onClick={nextMonth}
              className="p-1 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4] hover:bg-white/[0.04]"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Action button with single icon */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setCreateModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-white text-xs font-semibold transition-colors shadow-lg shadow-[#D42B92]/20"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Create</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center justify-between gap-4">
          <span>{error}</span>
          <button
            onClick={loadCalendarData}
            className="px-3 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-xs font-semibold transition-colors shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {/* Main Grid: Calendar Month on Left + Agenda on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Month Grid (7 cols) */}
        <div className="lg:col-span-7 bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
          {/* Day of Week Headers */}
          <div className="grid grid-cols-7 text-center text-xs font-semibold text-[#8E8E99] pb-2 border-b border-[#2C2C34]">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
              <div key={idx}>{day}</div>
            ))}
          </div>

          {/* Month Day Cells */}
          <div className="grid grid-cols-7 gap-1.5 text-xs">
            {daysArray.map((cellDate, idx) => {
              if (!cellDate) {
                return <div key={`empty-${idx}`} className="h-14 sm:h-16" />;
              }

              const isToday = isSameDay(cellDate, new Date());
              const isSelected = isSameDay(cellDate, selectedDate);
              const dayItems = items.filter((it) => getItemDateStr(it) === getCellDateStr(cellDate));

              return (
                <div
                  key={cellDate.toISOString()}
                  onClick={() => setSelectedDate(cellDate)}
                  className={`h-14 sm:h-16 p-1.5 rounded-xl border flex flex-col justify-between cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-[#321129] border-[#D42B92] shadow-md shadow-[#D42B92]/10'
                      : isToday
                      ? 'bg-white/[0.04] border-[#D42B92]/40'
                      : 'bg-[#08080A]/40 border-[#2C2C34]/60 hover:border-[#2C2C34]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-xs font-semibold ${
                        isSelected
                          ? 'text-[#D42B92]'
                          : isToday
                          ? 'text-white'
                          : 'text-[#8E8E99]'
                      }`}
                    >
                      {cellDate.getDate()}
                    </span>
                    {dayItems.length > 0 && (
                      <span className="text-[10px] text-[#8E8E99] font-mono">
                        {dayItems.length}
                      </span>
                    )}
                  </div>

                  {/* Colored Item Markers matching PDF Page 6 */}
                  <div className="flex flex-wrap gap-1">
                    {dayItems.slice(0, 3).map((it) => (
                      <span
                        key={it.id}
                        className={`w-2 h-2 rounded-full ${
                          it.type === 'Meeting'
                            ? 'bg-sky-400'
                            : it.type === 'Deadline'
                            ? 'bg-[#D42B92]'
                            : 'bg-amber-400'
                        }`}
                        title={`${it.type}: ${it.title}`}
                      />
                    ))}
                    {dayItems.length > 3 && (
                      <span className="text-[9px] text-[#8E8E99] leading-none">+</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Color Key matching PDF Page 6 */}
          <div className="flex flex-wrap items-center gap-4 text-xs text-[#8E8E99] pt-3 border-t border-[#2C2C34]/60">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-sky-400" />
              <span>Meeting (date + time + link)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#D42B92]" />
              <span>Deadline (date-only milestone)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
              <span>Task (work item with status)</span>
            </div>
          </div>
        </div>

        {/* Right Column: Chronological Agenda matching PDF Page 6 (5 cols) */}
        <div className="lg:col-span-5 bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4 flex flex-col">
          {/* Header & Filter Row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-[#2C2C34]">
            <h2 className="text-sm font-bold text-[#F2F2F4]">Agenda, by date</h2>
            <div className="flex items-center gap-2">
              <select
                value={selectedStartupId}
                onChange={(e) => setSelectedStartupId(e.target.value)}
                className="px-2.5 py-1.5 rounded-lg bg-[#08080A] border border-[#2C2C34] text-xs text-[#F2F2F4] focus:outline-none focus:border-[#D42B92] max-w-[155px]"
              >
                <option value="">All my items</option>
                <option value="my_tasks">My tasks</option>
                <option value="startups">All assigned startups</option>
                {assignedStartups.length > 0 && (
                  <optgroup label="Specific startup">
                    {assignedStartups.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>

              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-2.5 py-1.5 rounded-lg bg-[#08080A] border border-[#2C2C34] text-xs text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
              >
                <option value="all">All types</option>
                <option value="meeting">Meetings</option>
                <option value="deadline">Deadlines</option>
                <option value="task">Tasks</option>
              </select>
            </div>
          </div>

          {/* Agenda List */}
          <div className="flex-1 overflow-y-auto space-y-4 max-h-[500px] pr-1">
            {Object.keys(groupedAgenda).length === 0 ? (
              <div className="py-16 text-center text-xs text-[#8E8E99]">
                No items scheduled in this range.
              </div>
            ) : (
              Object.entries(groupedAgenda).map(([dateStr, dayItems]) => {
                const isSelectedDay = isSameDay(new Date(dateStr), selectedDate);

                return (
                  <div key={dateStr} className="space-y-2">
                    {/* Date Heading */}
                    <div
                      className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-md flex items-center justify-between ${
                        isSelectedDay
                          ? 'bg-[#321129] text-[#D42B92] border border-[#D42B92]/30'
                          : 'text-[#8E8E99]'
                      }`}
                    >
                      <span>{dateStr}</span>
                      {isSameDay(new Date(dateStr), new Date()) && <span>TODAY</span>}
                    </div>

                    {/* Day Items */}
                    <div className="space-y-2">
                      {dayItems.map((item) => {
                        const itemDate = new Date(item.date);
                        const timeStr =
                          item.type === 'Meeting'
                            ? itemDate.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })
                            : 'All day';

                        return (
                          <div
                            key={item.id}
                            onClick={() => setSelectedItemDetail(item)}
                            className="p-3 rounded-xl bg-[#08080A] border border-[#2C2C34] hover:border-[#D42B92]/40 transition-colors flex items-center justify-between gap-3 cursor-pointer group"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] font-semibold text-[#8E8E99]">
                                  {timeStr}
                                </span>
                                <span
                                  className={`text-[9px] font-extrabold uppercase px-1.5 py-0.2 rounded ${
                                    item.type === 'Meeting'
                                      ? 'bg-sky-500/20 text-sky-300'
                                      : item.type === 'Deadline'
                                      ? 'bg-rose-500/20 text-rose-300'
                                      : 'bg-amber-500/20 text-amber-300'
                                  }`}
                                >
                                  {item.type}
                                </span>
                              </div>
                              <div className="text-xs font-semibold text-[#F2F2F4] group-hover:text-[#D42B92] transition-colors truncate mt-1">
                                {item.title}
                              </div>
                              <div className="text-[11px] text-[#8E8E99] truncate">
                                {item.startupName}
                              </div>
                            </div>

                            {/* Action Join for Meetings */}
                            {item.type === 'Meeting' && item.meetingLink && item.status !== 'Cancelled' && (
                              <a
                                href={item.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="px-3 py-1 rounded-full bg-[#D42B92] hover:bg-[#b02279] text-white text-[11px] font-semibold transition-colors flex items-center gap-1 shadow-sm shrink-0"
                              >
                                <span>Join</span>
                                <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Item Detail Modal */}
      {selectedItemDetail && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
          <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl w-full max-w-md p-6 space-y-4 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <span
                  className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded ${
                    selectedItemDetail.type === 'Meeting'
                      ? 'bg-sky-500/20 text-sky-300'
                      : selectedItemDetail.type === 'Deadline'
                      ? 'bg-rose-500/20 text-rose-300'
                      : 'bg-amber-500/20 text-amber-300'
                  }`}
                >
                  {selectedItemDetail.type}
                </span>
                <h3 className="text-lg font-bold text-[#F2F2F4] mt-1.5">
                  {selectedItemDetail.title}
                </h3>
                <div className="text-xs text-[#8E8E99]">{selectedItemDetail.startupName}</div>
              </div>
              <button
                onClick={() => setSelectedItemDetail(null)}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2 text-xs text-[#8E8E99] bg-[#08080A] p-3.5 rounded-xl border border-[#2C2C34]">
              <div>
                <strong className="text-[#F2F2F4]">Scheduled:</strong>{' '}
                {new Date(selectedItemDetail.date).toLocaleString()}
              </div>
              {selectedItemDetail.description && (
                <div>
                  <strong className="text-[#F2F2F4]">Description:</strong>{' '}
                  {selectedItemDetail.description}
                </div>
              )}
              {selectedItemDetail.meetingLink && (
                <div>
                  <strong className="text-[#F2F2F4]">Link:</strong>{' '}
                  <a
                    href={selectedItemDetail.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#D42B92] hover:underline"
                  >
                    {selectedItemDetail.meetingLink}
                  </a>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              {selectedItemDetail.canEdit ? (
                <button
                  onClick={() => handleDeleteItem(selectedItemDetail)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs font-medium transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Delete</span>
                </button>
              ) : (
                <div />
              )}

              <div className="flex items-center gap-2">
                {selectedItemDetail.meetingLink && selectedItemDetail.status !== 'Cancelled' && (
                  <a
                    href={selectedItemDetail.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-1.5 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-white text-xs font-semibold transition-colors"
                  >
                    Join Meeting
                  </a>
                )}
                <button
                  onClick={() => setSelectedItemDetail(null)}
                  className="px-3 py-1.5 rounded-xl border border-[#2C2C34] text-xs text-[#8E8E99] hover:text-[#F2F2F4]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <CreateCalendarItemModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={loadCalendarData}
        assignedStartups={assignedStartups}
      />
    </div>
  );
}
